import { EventEmitterMixin } from './event_emitter';

/**
 * A mixin that adds form association capabilities to a custom element
 * @param {typeof HTMLElement} superClass - The class to extend
 * @returns {typeof HTMLElement} A class with form association capabilities
 */
export function FormAssociatedMixin(superClass) {
  class FormAssociatedElement extends EventEmitterMixin(superClass) {
    static formAssociated = true;

    __internals;

    /** @type {Array<import('../utils/validators').Validator>} */
    validators = [];

    /** @type {boolean} */
    _disabled = false;

    /** @type {boolean} */
    _invalid = false;

    /** @type {boolean} */
    _dirty = false;

    /** @type {any} */
    _defaultValue;

    static get properties() {
      return {
        name: { type: String, reflect: true },
        disabled: { type: Boolean, reflect: true },
        invalid: { type: Boolean, reflect: true },
      };
    }

    /** @returns {HTMLFormElement|null} The associated form element */
    get form() {
      return this.__internals.form;
    }

    /** @returns {ValidityState} The element's validity state */
    get validity() {
      return this.__internals.validity;
    }

    /** @returns {string} The validation message */
    get validationMessage() {
      return this.__internals.validationMessage;
    }

    /** @returns {boolean} Whether the element will be validated */
    get willValidate() {
      return this.__internals.willValidate;
    }

    /**
     * Sets the disabled state
     * @param {boolean} value - The disabled state
     */
    set disabled(value) {
      this._disabled = value;
      this.toggleAttribute('disabled', Boolean(this._disabled));
    }

    /** @returns {boolean} The disabled state */
    get disabled() {
      return this._disabled;
    }

    /**
     * Sets the invalid state
     * @param {boolean} value - The invalid state
     */
    set invalid(value) {
      this._invalid = value;
      this.toggleAttribute('invalid', Boolean(this._invalid));
    }

    /** @returns {boolean} The invalid state */
    get invalid() {
      return this._invalid;
    }

    constructor(...args) {
      super(args);
      this.__internals = this.attachInternals();
      this.addEventListener('invalid', this.handleInvalid);
    }

    /** @inheritdoc */
    connectedCallback() {
      super.connectedCallback();
      this._dirty = false;
      this.setDefaultValue();
    }

    /** Sets the default value of the form control */
    setDefaultValue() {
      if ('checked' in this) {
        this._defaultValue = this.checked;
      } else if ('value' in this) {
        this._defaultValue = this.value;
      }
    }

    /** Restores the form control to its default value */
    restoreDefaultValue() {
      if ('checked' in this) {
        this.checked = this._defaultValue;
      } else if ('value' in this) {
        this.value = this._defaultValue;
      }
    }

    /**
     * Handles invalid events
     * @param {Event} event - The invalid event
     */
    handleInvalid = (event) => {
      event.preventDefault();
      this.invalid = true;
    };

    /**
     * Sets the form value and state
     * @param {any} value - The form value
     * @param {any} [state] - The form state
     */
    setFormValue(value, state) {
      this.__internals.setFormValue(value, state || value);
    }

    /**
     * Sets the validity state
     * @param {ValidityStateFlags} flags - The validity flags
     * @param {string} message - The validation message
     * @param {HTMLElement} [anchor] - The element to anchor the validation popup to
     */
    setValidity(flags, message, anchor) {
      this.__internals.setValidity(flags, message, anchor);
    }

    /** Called when the form is reset */
    formResetCallback() {
      this.restoreDefaultValue();
      this._dirty = false;

      // Apply any changes happening during form reset synchronously
      this.performUpdate();

      this.invalid = false;
    }

    /**
     * Called when the form's disabled state changes
     * @param {boolean} state - The new disabled state
     */
    formDisabledCallback(state) {
      this._disabled = state;
      this.requestUpdate();
    }

    /**
     * Executes the component validators and updates the internal validity state
     * @param {string} [message] - Custom validation message
     */
    updateValidity(message) {
      const validity = {};
      let validationMessage = '';

      const errorMessages = [];

      for (const validator of this.validators) {
        const isValid = validator.isValid(this);

        validity[validator.key] = !isValid;

        if (!isValid) {
          errorMessages.push(
            typeof validator.message === 'function'
              ? validator.message(this)
              : validator.message
          );
        }
      }

      if (message) {
        validity.customError = true;
        errorMessages.push(message);
      }

      validationMessage = errorMessages.join(' & ');

      this.__internals.setValidity(validity, validationMessage);
      this.emitEvent('validitychange');
    }

    /** Updates the invalid state based on validity */
    setInvalidState() {
      if (this.hasUpdated || this._dirty) {
        this.invalid = !this.checkValidity();
      }
    }

    /**
     * Reports the element's validity to the user
     * @returns {boolean} True if the element is valid, false otherwise
     */
    reportValidity() {
      return this.__internals.reportValidity();
    }

    /**
     * Checks if the element is valid without reporting
     * @returns {boolean} True if the element is valid, false otherwise
     */
    checkValidity() {
      return this.__internals.checkValidity();
    }

    /**
     * Sets a custom validation message
     * @param {string} message - The validation message
     */
    setCustomValidity(message) {
      this.updateValidity(message);
      this.invalid = !this.checkValidity();
    }
  }

  return FormAssociatedElement;
}
